import { useEffect } from "react";
import { Route, Routes, Link, useLocation } from "react-router-dom";
import RouteComponent from "./routes";
import { ProvideAuth } from "./context/AuthContext";
import { ProvideTutorial } from "./context/TutorialContext";
import { ProvideCodeGeneration } from "./context/CodeGenerationContext";
import TermsAcceptance from "./components/TermsAcceptance";
import SubscriptionAlertBanner from "./components/SubscriptionAlertBanner";
import GenerationProgressBanner from "./components/GenerationProgressBanner";
import { CodeGenerationModal } from "./components/CodeGenerationModal";
import { CodeGenerationGuard } from "./components/CodeGenerationGuard";
import BrowserTranslateHint from "./components/BrowserTranslateHint";
import ConnectivityCheck from "./components/ConnectivityCheck";
import { initAmplitude, initGoogleAnalytics, track } from "@crm/analytics";
import { TooltipProvider } from "@shadcn-in-nx/ui/tooltip";
import { HelmetProvider } from "react-helmet-async";

export function useAmplitudePageView() {
    const location = useLocation();

    useEffect(() => {
        track("Page View", {
            path: location.pathname,
            page_path: window.location.pathname,
        });
    }, [location.pathname]);
}

export function App() {
    useAmplitudePageView();
    useEffect(() => {
        initAmplitude({
            pageViews: false,
            sessionReplay: true,
        });

        initGoogleAnalytics({});
    }, []);

    // Capture and store UTM parameters from URL
    useEffect(() => {
        if (typeof window === "undefined") return;

        const urlParams = new URLSearchParams(window.location.search);
        const utmParams = {
            utm_source: urlParams.get("utm_source"),
            utm_medium: urlParams.get("utm_medium"),
            utm_campaign: urlParams.get("utm_campaign"),
            utm_term: urlParams.get("utm_term"),
            utm_content: urlParams.get("utm_content"),
        };

        // Only save if at least one UTM parameter exists
        const hasUtmParams = Object.values(utmParams).some(
            (value) => value !== null
        );

        if (hasUtmParams) {
            try {
                // Store UTM parameters in localStorage
                // Filter out null values to preserve first-touch attribution
                const validUtmParams = Object.fromEntries(
                    Object.entries(utmParams).filter(
                        ([_, value]) => value !== null
                    )
                );

                const existingUtm = localStorage.getItem("utm_params");
                if (existingUtm) {
                    const existing = JSON.parse(existingUtm);
                    // Merge, only overwriting with new non-null values
                    // This preserves first-touch attribution - existing values won't be overwritten by null
                    const merged = { ...existing, ...validUtmParams };
                    localStorage.setItem("utm_params", JSON.stringify(merged));
                } else {
                    // Only store non-null values on first save
                    localStorage.setItem(
                        "utm_params",
                        JSON.stringify(validUtmParams)
                    );
                }
            } catch (error) {
                console.log(
                    "Error saving UTM parameters to localStorage:",
                    error
                );
            }
        }
    }, []);

    return (
        <HelmetProvider>
            <TooltipProvider>
                <ProvideAuth>
                    <ProvideCodeGeneration>
                        <ProvideTutorial>
                            <SubscriptionAlertBanner />
                            <GenerationProgressBanner />
                            <TermsAcceptance />
                            <CodeGenerationGuard />
                            <RouteComponent />
                            <CodeGenerationModal />
                            <BrowserTranslateHint />
                            <ConnectivityCheck />
                        </ProvideTutorial>
                    </ProvideCodeGeneration>
                </ProvideAuth>
            </TooltipProvider>
        </HelmetProvider>
    );
}

export default App;
